import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Team() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [seatCount, setSeatCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  // Last invite created — shown as a beautiful inline panel with copy/share buttons,
  // regardless of whether Resend successfully delivered the email.
  const [lastInvite, setLastInvite] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/teams/me");
      setTeam(data.team);
      setMembers(data.members || []);
      setInvites(data.invites || []);
      setSeatCount(data.seat_count || 0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const createTeam = async () => {
    if (!newName.trim()) return toast.error("Please enter a team name.");
    setCreating(true);
    try {
      await api.post("/teams", { name: newName.trim() });
      toast.success("Team created.");
      setNewName("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create team.");
    } finally { setCreating(false); }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return toast.error("Enter an email.");
    setInviting(true);
    try {
      const { data } = await api.post("/teams/invite", {
        email: inviteEmail.trim(),
        origin_url: window.location.origin,
      });
      // Always show the invite link inline — the source of truth is the link itself.
      // Email delivery is best-effort; if it fails the user still has the link.
      setLastInvite({
        email: inviteEmail.trim(),
        link: data.invite_link,
        emailed: !!data.sent,
      });
      if (data.sent) {
        toast.success(`Invitation emailed to ${inviteEmail.trim()}. Link is also shown below.`);
      } else {
        toast.message("Email couldn't be sent automatically — your invite link is shown below. Copy & share it directly.", { duration: 8000 });
      }
      setInviteEmail("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invite.");
    } finally { setInviting(false); }
  };

  const copyInviteLink = async () => {
    if (!lastInvite?.link) return;
    try {
      await navigator.clipboard.writeText(lastInvite.link);
      toast.success("Invite link copied!");
    } catch {
      toast.message("Copy this link manually", { description: lastInvite.link, duration: 12000 });
    }
  };

  const cancelInvite = async (email) => {
    try {
      await api.delete(`/teams/invites/${encodeURIComponent(email)}`);
      toast.success("Invite canceled.");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeMember = async (memberId) => {
    if (!confirm("Remove this teammate? Their seat charge will stop on next billing cycle.")) return;
    try {
      await api.delete(`/teams/members/${memberId}`);
      toast.success("Member removed.");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const leaveTeam = async () => {
    if (!confirm("Leave this team?")) return;
    try {
      await api.post("/teams/leave");
      toast.success("You left the team.");
      navigate("/dashboard");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const disbandTeam = async () => {
    if (!confirm("Disband this team? All members will lose access and the seat subscription will be cancelled.")) return;
    try {
      await api.post("/teams/disband");
      toast.success("Team disbanded.");
      navigate("/dashboard");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div style={{ maxWidth: 880, margin: "60px auto", padding: 24 }}><div className="shimmer" style={{ height: 180, borderRadius: 14 }} /></div>;

  if (!team) {
    return (
      <div style={{ maxWidth: 720, margin: "60px auto", padding: 24 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 8 }}>FUNERAL HOME TEAM</div>
        <h1 className="serif" style={{ fontSize: 38, color: "#fff", margin: 0, fontWeight: 400 }}>Create a shared workspace</h1>
        <p className="muted" style={{ marginTop: 12, lineHeight: 1.7 }}>
          Invite your coworkers so they can view and contribute to obituaries together. Your subscription covers your seat;
          each additional teammate is <b style={{ color: "var(--gold)" }}>$7 / month</b>, prorated automatically.
        </p>
        <div className="bg-card" style={{ padding: 24, marginTop: 22 }}>
          <label className="muted" style={{ fontSize: 12, letterSpacing: "0.1em" }}>FUNERAL HOME / TEAM NAME</label>
          <input
            data-testid="team-create-name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="e.g., Pierce Family Funeral Services"
            style={{ width: "100%", padding: 14, marginTop: 8, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8, fontSize: 16 }}
          />
          <button data-testid="team-create-btn" onClick={createTeam} disabled={creating} className="btn-gold" style={{ marginTop: 16, width: "100%" }}>
            {creating ? "Creating…" : "Create team"}
          </button>
        </div>
      </div>
    );
  }

  const isOwner = team.owner_user_id === user?.id;

  return (
    <div style={{ maxWidth: 900, margin: "40px auto", padding: "0 24px" }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 8 }}>YOUR TEAM</div>
        <h1 className="serif" style={{ fontSize: 38, color: "#fff", margin: 0, fontWeight: 400 }}>{team.name}</h1>
        <div className="muted" style={{ marginTop: 6, fontSize: 14 }}>
          {members.length} member{members.length === 1 ? "" : "s"} · {seatCount} paid seat{seatCount === 1 ? "" : "s"} ($7 each / month)
        </div>
      </div>

      {isOwner && (
        <div className="bg-card" style={{ padding: 22, marginBottom: 22 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 12 }}>INVITE A COWORKER</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              data-testid="team-invite-email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="coworker@funeralhome.com"
              style={{ flex: 1, minWidth: 240, padding: 12, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8 }}
            />
            <button data-testid="team-invite-btn" onClick={sendInvite} disabled={inviting} className="btn-gold">
              {inviting ? "Sending…" : "Send invite"}
            </button>
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
            We&apos;ll email them a 14-day link AND show you the link to share directly. When they accept, $7/mo is added to your bill.
          </div>

          {lastInvite && (
            <div data-testid="team-invite-link-card" style={{
              marginTop: 18, padding: 18,
              background: lastInvite.emailed ? "#0e1a10" : "#1a1408",
              border: "1px solid " + (lastInvite.emailed ? "#2a4a2a" : "#5a4a1a"),
              borderRadius: 12,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "var(--gold)", marginBottom: 2 }}>
                    {lastInvite.emailed ? "✓ INVITE SENT" : "📋 INVITE READY TO SHARE"}
                  </div>
                  <div style={{ color: "#fff", fontSize: 14 }}>For <b>{lastInvite.email}</b></div>
                </div>
                <button data-testid="team-invite-link-close" onClick={() => setLastInvite(null)} className="btn-ghost" style={{ fontSize: 11, padding: "4px 10px" }}>Dismiss</button>
              </div>
              <div style={{
                fontFamily: "monospace", fontSize: 11, padding: "10px 12px",
                background: "#000", border: "1px solid #333", borderRadius: 6,
                color: "#ddd", wordBreak: "break-all", marginBottom: 10,
              }}>{lastInvite.link}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button data-testid="team-invite-copy-btn" onClick={copyInviteLink} className="btn-gold" style={{ fontSize: 12, padding: "8px 14px" }}>Copy link</button>
                <a
                  data-testid="team-invite-mailto-btn"
                  href={`mailto:${encodeURIComponent(lastInvite.email)}?subject=${encodeURIComponent("You're invited to our Obituary Relief team")}&body=${encodeURIComponent(`Hi,\n\nClick this link to join our team on Obituary Relief (expires in 14 days):\n\n${lastInvite.link}\n\nThanks!`)}`}
                  className="btn-ghost" style={{ fontSize: 12, padding: "8px 14px", textDecoration: "none" }}
                >Email it</a>
              </div>
              {!lastInvite.emailed && (
                <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
                  Note: our email sender (Resend) couldn&apos;t deliver this one automatically — share the link above and they&apos;re in.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="bg-card" style={{ padding: 22, marginBottom: 22 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 14 }}>MEMBERS</div>
        {members.map(m => (
          <div key={m.id} data-testid={`team-member-${m.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #1a1a1a" }}>
            <div>
              <div style={{ color: "#fff", fontSize: 15 }}>{m.name || m.email}</div>
              <div className="muted" style={{ fontSize: 12 }}>{m.email} · {m.team_role}</div>
            </div>
            {isOwner && m.team_role !== "owner" && (
              <button data-testid={`team-remove-${m.id}`} onClick={() => removeMember(m.id)} className="btn-ghost" style={{ fontSize: 12, padding: "6px 12px" }}>Remove</button>
            )}
          </div>
        ))}
      </div>

      {isOwner && invites.length > 0 && (
        <div className="bg-card" style={{ padding: 22, marginBottom: 22 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 14 }}>PENDING INVITES</div>
          {invites.map(inv => (
            <div key={inv.email} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #1a1a1a" }}>
              <div style={{ color: "#bbb" }}>{inv.email}</div>
              <button data-testid={`team-cancel-${inv.email}`} onClick={() => cancelInvite(inv.email)} className="btn-ghost" style={{ fontSize: 12, padding: "6px 12px" }}>Cancel</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 28, justifyContent: "flex-end" }}>
        <Link to="/dashboard" className="btn-ghost" style={{ textDecoration: "none" }}>Back to dashboard</Link>
        {isOwner ? (
          <button data-testid="team-disband-btn" onClick={disbandTeam} className="btn-ghost" style={{ color: "#FF6347", borderColor: "#FF6347" }}>Disband team</button>
        ) : (
          <button data-testid="team-leave-btn" onClick={leaveTeam} className="btn-ghost" style={{ color: "#FF6347", borderColor: "#FF6347" }}>Leave team</button>
        )}
      </div>
    </div>
  );
}
